import breakOut from '@sparta.global.utilities/sparta-breakout-utility/dist/es6.js';
import spartaEventsUtility from '@sparta.global.utilities/sparta-events-utility/dist/es6.js';
import spartaGlobalFunctions from '@sparta.global.utilities/sparta-global-functions-utility/dist/es6.js';
import spartaStoreUtility from '@sparta.global.utilities/sparta-store-utility/dist/es6.js';
import Handlebars from '@sparta.global.vendors/handlebars';

/* global breakOut spartaEventsUtility spartaStoreUtility */
/* eslint-disable no-param-reassign */

/**
 * Namespace for the Sparta Squeezebox Utility.
 * @constant {string}
 */
const utilityNamespace = 'spartaSqueezeboxUtility';

/**
 * Allow innerPanel event listener to fire first time and prevent on additional
 */
const instance = {};

/**
 * Collection of namespaced event strings.
 * @constant {object}
 */
const EVENTS = {
  SQUEEZEBOX_INITIALIZED: `SQUEEZEBOX_INITIALIZED.${utilityNamespace}`,
  SQUEEZEBOX_INITIALIZED_FAQ: `SQUEEZEBOX_INITIALIZED_FAQ.${utilityNamespace}`,
  SQUEEZEBOX_READY: `SQUEEZEBOX_READY.${utilityNamespace}`,
  SQUEEZEBOX_ITEM_HIDE: `SQUEEZEBOX_ITEM_HIDE.${utilityNamespace}`,
  SQUEEZEBOX_ITEM_SHOW: `SQUEEZEBOX_ITEM_SHOW.${utilityNamespace}`,
  SQUEEZEBOX_SHOW_ALL: `SQUEEZEBOX_SHOW_ALL.${utilityNamespace}`,
  SQUEEZEBOX_HIDE_ALL: `SQUEEZEBOX_HIDE_ALL.${utilityNamespace}`
};

/**
 * Collection of numeric keycodes for input handlers.
 * @constant {object}
 */
const KEYS = {
  ENTER: 13,
  SPACE: 32,
  UP: 38,
  DOWN: 40
};

/**
 * Collection of possible messages to show the user.
 * @constant {object}
 */
const STRINGS = {
  NO_PANEL_CONTENT_ERROR: '<strong>Error:</strong> No panel content available.'
};

/**
 * The length of panel open/close animations in milliseconds.
 * @constant {number}
 */
const PANEL_ANIMATION_LENGTH = 300;

/**
 * Extra spacing to add (in pixels) when scrolling an item into view.
 * @constant {number}
 */
const SCROLL_OFFSET_PADDING = 10;

/**
 * The Squeezebox Utility allows large amounts of page content to be displayed in a limited area.
 */
class SpartaSqueezebox {
  /**
   * Finds all squeezeboxes and applies event listeners to the panels.
   * @returns {void}
   */
  init() {
    // Initialized squeezeboxes.
    this.squeezeboxes = [];
    this.updateClassObject();

    // Activate (possibly deep-linked) squeezebox and scroll to its content.
    spartaEventsUtility.sub(EVENTS.SQUEEZEBOX_INITIALIZED, payload => {
      setTimeout(() => {
        this.activateSection();
        spartaEventsUtility.pub(EVENTS.SQUEEZEBOX_READY, payload.data);
      }, PANEL_ANIMATION_LENGTH);
      window.addEventListener('hashchange', this.activateSection.bind(this), false);
      window.addEventListener('popstate', this.activateSection.bind(this), false);
    });

    // Initialize language and language-specific content
    this.language = spartaGlobalFunctions.getLanguage();
    if (spartaStoreUtility.getData('spaParams')) {
      this.content = spartaStoreUtility.getData('spaParams').global[this.language].squeezeboxes;
    }

    // Apply event listeners to each panel.
    const squeezeboxPanels = document.querySelectorAll('.panel-header');

    // If print is clicked, and allowed, open all squeezeboxes prior to print event.
    spartaEventsUtility.sub('ON_WINDOW_PRINT.PrintLinkModule', () => {
      const printSqueezeBoxes = [...squeezeboxPanels];
      if (printSqueezeBoxes.length > 0) {
        printSqueezeBoxes.forEach(element => {
          const hasExpanded = element.getAttribute('aria-expanded') === 'false';
          const togglePrint = element.getAttribute('data-print-show-all');
          if (hasExpanded && (!togglePrint || togglePrint === 'true')) {
            this.activatePanel(element);
          }
        });
      }
    });
    Array.prototype.forEach.call(squeezeboxPanels, item => {
      // Prefetch to make sure the on-click interaction is as light as possible.
      const parent = item.closest('.squeezebox-wrapper');
      const panelId = item.getAttribute('data-panel');
      const panel = parent.querySelector(`#${panelId}`);
      const isInitialized = item.getAttribute('data-initialized');

      // create init instance based off of parent ID
      instance[parent.id] = {};
      if (!isInitialized) {
        // Initialize active-state ADA attributes and hidden text.
        const panelIsActive = panel.classList.contains('active');
        this.setActiveState(panel, panelIsActive);

        // Check for dynamic module content and move it to the panel.
        this.getModuleContent(panel);

        // Open the panel when its header is clicked or keyboard-activated.
        item.addEventListener('click', () => {
          this.setHiddenStateText(panel, !panel.classList.contains('active'));
          this.activatePanel(item);
        });
        item.addEventListener('keydown', event => {
          const navPressed = this.navKeyPressed(event);
          const code = event.charCode || event.keyCode;

          // Only listen for a nav key or the down/up arrows (which specifically open or close).
          let forceState = null;
          if (code === KEYS.DOWN) {
            forceState = true;
          } else if (code === KEYS.UP) {
            forceState = false;
          }
          if (!navPressed && forceState === null) {
            return;
          }
          event.preventDefault();
          this.activatePanel(item, forceState);
        });

        // Add squeezebox to the this.squeezeboxes array.
        if (!this.squeezeboxes.includes(parent.getAttribute('id'))) {
          this.squeezeboxes.push(parent.getAttribute('id'));
        }
        item.setAttribute('data-initialized', true);
      }
    });

    // Apply event listeners to each squeezebox group controls link.
    // Breakout links are for SEO to reveal squeezebox items and allow them to be crawled correctly
    const breakOutLinks = document.querySelectorAll('.spa-hidden[data-breakout]');
    Array.prototype.forEach.call(breakOutLinks, link => {
      link.addEventListener('click', () => {
        this.exposeAllPanels(link);
      });
      link.addEventListener('keydown', event => {
        if (this.navKeyPressed(event)) {
          event.preventDefault();
          this.exposeAllPanels(link);
        }
      });
    });
    const expandAllLinks = document.querySelectorAll('.squeezebox-expand-all-link');
    Array.prototype.forEach.call(expandAllLinks, link => {
      link.addEventListener('click', () => {
        this.showAllPanels(link, false);
      });
      link.addEventListener('keydown', event => {
        if (this.navKeyPressed(event)) {
          event.preventDefault();
          this.showAllPanels(link, false);
        }
      });
    });
    const collapseAllLinks = document.querySelectorAll('.squeezebox-collapse-all-link');
    Array.prototype.forEach.call(collapseAllLinks, link => {
      link.addEventListener('click', () => {
        this.hideAllPanels(link);
      });
      link.addEventListener('keydown', event => {
        if (this.navKeyPressed(event)) {
          event.preventDefault();
          this.hideAllPanels(link);
        }
      });
    });
    const expandCollapseControls = document.querySelectorAll('.squeezebox-group-control-wrapper');
    Array.prototype.forEach.call(expandCollapseControls, squeezeboxGroup => {
      this.updateSqueezeboxGroupState(squeezeboxGroup.closest('.squeezebox-wrapper'));
    });

    // If utility is included, invoke prerender/breakout event listener.
    if (typeof breakOut === 'object') {
      breakOut.init();
    }

    // based on JCR and innerPanels add API to toggle innerPanels from parent level control
    if (expandCollapseControls) {
      const controlsArray = [...expandCollapseControls];
      controlsArray.forEach(element => {
        const wrapperElm = element.parentElement;
        const expandCollapseInnerPanels = wrapperElm.getAttribute('data-expand-collapse-inner-panels');

        // if we're not toggling innerPanels kill check
        if (expandCollapseInnerPanels?.toString() !== 'true') {
          return;
        }

        // add event listeners to control innerPanels
        this.expandCollapseInnerPanels(wrapperElm);
      });
    }

    // Notify SB sequence is done
    spartaEventsUtility.pub(EVENTS.SQUEEZEBOX_INITIALIZED, this.squeezeboxes);

    // Support for global FAQ module
    spartaEventsUtility.pub(EVENTS.SQUEEZEBOX_INITIALIZED_FAQ, this);
  }

  /**
   * Accessibility for keyboard users.
   * @param {object} event - The browser's `keydown` event.
   * @returns {boolean} An indication of whether the key pressed was enter or the spacebar.
   */
  navKeyPressed(event) {
    const code = event.charCode || event.keyCode;
    return code === KEYS.ENTER || code === KEYS.SPACE;
  }

  /**
   * Scrolls to a given element by slug ID, accounting for sticky bar height.
   * @param {string} target - Optional slug ID for the target panel. Defaults to location hash.
   * @returns {void}
   */
  activateSection(target) {
    // Trim the supplied hash down to one or two stub ID values.
    let trimTarget = typeof target === 'string' ? target : window.location.hash;
    if (trimTarget && trimTarget[0] === '#') {
      trimTarget = trimTarget.substr(1);
    }
    const targetIds = trimTarget.split('__');

    // Find the panel that matches the supplied slug ID, or just quit now.
    const targetId = targetIds[0];
    const panel = document.querySelector(`.squeezebox-panel[data-slug="${targetId}"]`);
    if (!panel) {
      return;
    }

    // If this is a nested child panel, first expand its parent.
    const parentPanel = panel.parentNode.closest('.squeezebox-panel');
    this.activatePanel(parentPanel, true);

    // Now expand the panel itself.
    this.activatePanel(panel, true);

    // Calculate the height of any sticky bars to avoid when scrolling to a given section.
    this.scrollOffset = SCROLL_OFFSET_PADDING;
    const stickyEls = document.querySelectorAll('.spa-sticky');
    Array.prototype.forEach.call(stickyEls, el => {
      this.scrollOffset += el.offsetHeight;
    });

    // Scroll to the indicated section, but only if we're down to a single ID.
    if (targetIds.length === 1) {
      setTimeout(() => {
        const targetEl = document.querySelector(`[data-slug="${targetIds[0]}"]`);
        this.scrollTo(targetEl);
      }, PANEL_ANIMATION_LENGTH);
    }

    // If there is a second part to the supplied target, treat it the same as the first.
    if (targetIds.length > 1 && targetIds[1]) {
      this.activateSection(targetIds[1]);
    }
  }

  /**
   * Shows/hides panels based on `squeezebox-type` and `one-at-a-time` data options.
   * @param {object} target - The panel to be activated, by `panel-header` link or a parent.
   * @param {boolean} forceState - Optional. When set, specifies the panel's open/closed state.
   * @returns {void}
   */
  activatePanel(target, forceState) {
    if (!target || forceState && target.getAttribute('aria-expanded') === 'true') {
      return;
    }

    // Focus in on the `panel-header` link, regardless of what we're passed.
    let item = target;
    if (!item.classList.contains('panel-header')) {
      item = item.querySelector('.panel-header');
    }
    if (!item) {
      return;
    }

    // Ensure this panel is properly enclosed in an `squeezebox-wrapper`.
    const parent = item.closest('.squeezebox-wrapper');
    if (!parent) {
      return;
    }

    // Checking for proper panel structure. Not an issue if the helper is used.
    const panelId = item.getAttribute('data-panel');
    const panel = parent.querySelector(`#${panelId}`);
    if (!panel) {
      return;
    }
    const alreadyActive = panel.classList.contains('active');

    // If the panel is already active or being forced closed, collapse it.
    if (alreadyActive || forceState === false) {
      // Nothing to do if the forced panel is already in the desired state.
      if (!alreadyActive && forceState === false || forceState === true) {
        return;
      }
      this.setActiveState(panel, false);
      this.updateSqueezeboxGroupState(parent);
      spartaEventsUtility.pub(EVENTS.SQUEEZEBOX_ITEM_HIDE, {
        squeezebox: parent,
        item: panel
      });
      return;
    }

    // Otherwise, if the panel is not already active, expand it.
    // Start by closing any other open panels if necessary.
    const oneAtATime = parent.getAttribute('data-one-at-a-time') === 'true';
    if (oneAtATime) {
      // Close any other open panels.
      const els = document.querySelectorAll(`
        #${parent.id} > .squeezebox-panels > .squeezebox-panel
      `);
      Array.prototype.forEach.call(els, el => {
        // Skip over the current panel to avoid closing it unnecessarily.
        if (el === panel) {
          return;
        }
        this.setActiveState(el, false);
      });
    }

    // Then open the necessary panel.
    this.setActiveState(panel, true);
    this.updateSqueezeboxGroupState(parent);
    spartaEventsUtility.pub(EVENTS.SQUEEZEBOX_ITEM_SHOW, {
      squeezebox: parent,
      item: panel
    });
  }

  /**
   * Shows panels based on `squeezebox-type` and `one-at-a-time` options.
   * @param {object} link - The show/hide link triggering the event.
   * @param {boolean} overrideConfigurations - Indicates if the `squeezebox-type` and
   * `one-at-a-time` configuration settings should be ignored when showing the panels.
   * @returns {void}
   */
  showAllPanels(link, overrideConfigurations) {
    // Grab parent `squeezebox-wrapper` and hard set options to trigger CSS.
    const parent = link.closest('.squeezebox-wrapper');
    const panelOpen = link.closest('.squeezebox-wrapper.all-panels-open') ? true : false;

    // Stops open panels from opening again
    if (panelOpen) {
      return;
    }
    if (overrideConfigurations) {
      parent.setAttribute('data-one-at-a-time', '');
    }
    const parentDataAttributes = parent.dataset;

    // Show all panels, if the squeezebox group is configured to support it
    if (!parentDataAttributes.oneAtATime || parentDataAttributes.oneAtATime?.toString() === 'false') {
      // Grab all panels and apply the active state.
      const panels = parent.querySelectorAll(`
        #${parent.id} > .squeezebox-panels > .squeezebox-panel
      `);
      Array.prototype.forEach.call(panels, el => this.setActiveState(el, true));
      this.updateSqueezeboxGroupState(parent);
      spartaEventsUtility.pub(EVENTS.SQUEEZEBOX_SHOW_ALL, {});
    }
  }

  /**
   * Shows panels and sub panels.
   * @param {Object} showHideLink The show/hide link triggering the event.
   * @returns {void}
   */
  exposeAllPanels(showHideLink) {
    // Grab parent `squeezebox-wrapper` and hard set options to trigger CSS.
    const parent = showHideLink.closest('.squeezebox-wrapper');

    // Grab all panels and apply the active state.
    const panels = parent.querySelectorAll(`
      #${parent.id} > .squeezebox-panels > .squeezebox-panel
    `);
    Array.prototype.forEach.call(panels, el => this.setActiveState(el, true));
    this.updateSqueezeboxGroupState(parent);
  }

  /**
   * Hides panels based on `squeezebox-type` and `one-at-a-time` options.
   * @param {Object} link - The show/hide link triggering the event.
   * @returns {void}
   */
  hideAllPanels(link) {
    // Grab parent `squeezebox-wrapper` and hard set options to trigger CSS.
    const parent = link.closest('.squeezebox-wrapper');
    const parentDataAttributes = parent.dataset;
    const expandAllLink = parent ? parent.querySelector('.squeezebox-expand-all-link') : null;
    if (!expandAllLink) {
      return;
    }

    // Show all panels, if the squeezebox group is configured to support it.
    if (!parentDataAttributes.oneAtATime || parentDataAttributes.oneAtATime === 'false') {
      // Grab all panels and apply the active state.
      const panels = parent.querySelectorAll(`
        #${parent.id} > .squeezebox-panels > .squeezebox-panel
      `);
      Array.prototype.forEach.call(panels, el => this.setActiveState(el, false));
      this.updateSqueezeboxGroupState(parent);
      spartaEventsUtility.pub(EVENTS.SQUEEZEBOX_HIDE_ALL, {});
    }
  }

  /**
   * Shows or hides panels depending on which is needed.
   * @param {object} squeezeboxGroup - The show/hide link triggering the event.
   * @returns {void}
   */
  updateSqueezeboxGroupState(squeezeboxGroup) {
    const groupClasses = squeezeboxGroup.classList;
    const squeezeboxesAllOpenClass = 'all-panels-open';
    const squeezeboxesAllClosedClass = 'all-panels-closed';
    setTimeout(() => {
      const panelQuery = `#${squeezeboxGroup.id} > .squeezebox-panels > .squeezebox-panel`;
      const panelCount = squeezeboxGroup.querySelectorAll(panelQuery).length;
      const activePanelCount = squeezeboxGroup.querySelectorAll(`${panelQuery}.active`).length;
      groupClasses.remove(squeezeboxesAllOpenClass);
      groupClasses.remove(squeezeboxesAllClosedClass);
      if (activePanelCount === 0) {
        groupClasses.add(squeezeboxesAllClosedClass);
      } else if (panelCount !== 1 && activePanelCount === panelCount) {
        groupClasses.add(squeezeboxesAllOpenClass);
      }
    }, 20);
  }

  /**
   * Shows or hides panels depending on which is needed.
   * @param {object} link - The show/hide link triggering the event.
   * @returns {void}
   */
  togglePanels(link) {
    const expanded = link.getAttribute('data-expanded') === 'true';

    // Grab parent `squeezebox-wrapper` and hard set options to trigger CSS.
    const parent = link.closest('.squeezebox-wrapper');
    if (!expanded) {
      parent.setAttribute('data-squeezebox-type', 'squeezebox');
      parent.setAttribute('data-one-at-a-time', '');
    }

    // Switch the link's expanded state and alternate text.
    const modLink = link;
    const altText = link.getAttribute('data-alt-text') || 'Hide all';
    modLink.setAttribute('data-expanded', expanded ? '' : 'true');
    modLink.setAttribute('data-alt-text', link.textContent);
    modLink.textContent = altText;

    // Grab all panels and toggle the active state.
    const panels = parent.querySelectorAll('.squeezebox-panel');
    Array.prototype.forEach.call(panels, el => this.setActiveState(el, !expanded));
  }

  /**
   * Sets the `ada-hidden` panel message ("open" or "closed").
   * @param {object} el - The page element to be marked as "open" or "closed".
   * @param {boolean} active - Determines whether the element is active or not.
   * @returns {void}
   */
  setHiddenStateText(el, active) {
    // Find either the panel header that will contain open/closed text.
    const panelHeader = el.querySelector('.panel-header');

    // Add expanded/collpased state for screenreaders.
    if (panelHeader) {
      panelHeader.setAttribute('aria-expanded', active ? 'true' : 'false');
    }
  }

  /**
   * Sets the `active` class and other state markers for ADA compliance.
   * @param {object} el - The panel to be marked as "active" or not.
   * @param {boolean} active - Specifies whether the panel should be active.
   * @returns {void}
   */
  setActiveState(el, active) {
    // Ensure that the DOM element supplied is actually a squeezebox panel.
    if (!el.classList.contains('squeezebox-panel')) {
      return;
    }

    // Store a reference the panel content.
    const panelEl = el.querySelector('.panel-content');
    if (!panelEl) {
      return;
    }

    // Cancel toggling a panel if it's already opening or closing. This allows for double-clicks.
    if (el.getAttribute('data-animating')) {
      return;
    }

    // Update the ADA-hidden open/closed state text as early as possible.
    this.setHiddenStateText(el, active);

    // Temporarily replace vertical padding with a spacer. This is needed for height animation.
    el.setAttribute('data-animating', true);
    const panelStyle = window.getComputedStyle(panelEl);
    const spacerEl = document.createElement('div');
    spacerEl.style.height = panelStyle.paddingTop;
    panelEl.style.paddingTop = '0';
    panelEl.insertBefore(spacerEl, panelEl.firstChild);

    // Open or close the panel using a CSS animation.
    if (active) {
      // Show the panel by adding the `active` class. A timeout is needed for panel animation.
      panelEl.style.height = '0';
      requestAnimationFrame(() => {
        el.classList.add('active');
        panelEl.style.height = `${panelEl.scrollHeight}px`;
      });

      // If content is loaded dynamically, check for flag to see if spinner needs to be displayed.
      if (el && el.getAttribute('data-preserve')) {
        this.injectSqueezeboxLoader(el);
      }

      // Show panel.
      panelEl.removeAttribute('hidden');
    } else {
      // Hide the panel by removing the `active` class. A timeout is needed for panel animation.
      panelEl.style.paddingBottom = '0';
      panelEl.style.height = `${panelEl.scrollHeight}px`;
      requestAnimationFrame(() => {
        el.classList.remove('active');
        panelEl.style.height = '0';
      });
    }

    // Remove the panel's explicit styling and spacer when animation is no longer taking place.
    setTimeout(() => {
      panelEl.style.height = null;
      panelEl.style.paddingTop = null;
      panelEl.style.paddingBottom = null;
      spacerEl.remove();
      el.removeAttribute('data-animating');

      // Hide inactive panel content from screen readers.
      if (!active) {
        panelEl.setAttribute('hidden', true);
      }
    }, PANEL_ANIMATION_LENGTH);
  }

  /**
   * Checks for dynamic module content and appends it to the panel content.
   * @param {object} panel The panel element to be checked for dynamic module content.
   * @returns {void}
   */
  getModuleContent(panel) {
    const panelContent = panel.querySelector('[data-panel-content]');
    if (panelContent) {
      const moduleName = panelContent.getAttribute('data-panel-content');
      const moduleContent = document.querySelector(`[data-module="${moduleName}"]`);
      if (moduleContent) {
        panelContent.appendChild(moduleContent);
        moduleContent.style.display = 'block';
      } else {
        panelContent.innerHTML = STRINGS.NO_PANEL_CONTENT_ERROR;
      }
    }
  }

  /**
   * Clears squeezebox panel of DOM elements and adds a loading icon
   * @param {object} element - squeezebox panel DOM element
   * @returns {void}
   */
  injectSqueezeboxLoader(element) {
    // Get DOM elements, and define loader classes.
    const settings = {
      wrapper: element,
      preserveContent: element.getAttribute('data-preserve'),
      trigger: element.querySelector('.panel-header'),
      panelSpinner: 'squeezebox-panel__spinner',
      panelSpinnerHide: 'squeezebox-panel__spinner--hide',
      panelSpinnerIcon: 'squeezebox-panel__spinner-icon',
      panelSpinnerText: 'squeezebox-panel__spinner-text',
      panelSpinnerIconName: 'spa-icon-loading',
      panelSpinnerIconSize: 'spa-icon-loading-72'
    };
    settings.target = settings.trigger.nextElementSibling;

    // If content is already in DOM, then just show it.
    if (settings.target.textContent.trim().length !== 0 && settings.preserveContent === 'true') {
      return;
    }

    // Empty DOM area.
    settings.target.innerHTML = '';

    // Create spinner.
    if (settings.wrapper.getAttribute('data-spinner') === 'true') {
      this.dropdownCreateSpinner(settings.wrapper, settings);
    }

    // Reset panel height attribute so we can animate if need be.
    settings.target.style.paddingTop = null;
    setTimeout(() => {
      settings.target.style.height = `${settings.target.offsetHeight}px`;
    }, PANEL_ANIMATION_LENGTH + 1);
  }

  /**
   * Injects content dynamically into the targeted tabs content area
   * @param {object} element - target DOM element to get injected content
   * @param {string} string - string to be injected
   * @returns {void}
   */
  injectSqueezeBoxContent(element, string) {
    // Get DOM elements.
    const wrapper = element.parentElement;
    const target = element.nextElementSibling;
    const preserveContent = wrapper.getAttribute('data-preserve');

    // Remove spinner.
    const spinnerEl = wrapper.querySelector('.squeezebox-panel__spinner');
    if (spinnerEl) {
      spinnerEl.remove();
    }

    // Kill check if dropdown is meant to reload content.
    if (target.innerHTML.trim().length !== 0 && preserveContent === 'true') {
      return;
    }

    // Test if what we got is a string or object. If it's an object, set variables based off of it.
    const htmlSafeString = string;
    let callback;
    if (typeof htmlSafeString === 'object') {
      const safeStringHead = new Handlebars.SafeString(`${htmlSafeString.htmlHead}`).string;
      const safeStringBody = new Handlebars.SafeString(`${htmlSafeString.htmlBody}`).string;
      const safeStringFoot = new Handlebars.SafeString(`${htmlSafeString.htmlFoot}`).string;
      ({
        callback
      } = htmlSafeString);
      string = `
        <div class="squeezebox-panel-content-head">${safeStringHead}</div>
        <div class="squeezebox-panel-content-body">${safeStringBody}</div>
        <div class="squeezebox-panel-content-foot">${safeStringFoot}</div>
      `;
    } else {
      // Otherwise, we just got a string, so use it.
      string = htmlSafeString;
    }

    // Inject content.
    target.innerHTML = string;

    // Callback that may have been passed.
    if (typeof callback === 'function') {
      callback();
    }

    // Re-run CSS animation now that content is there.
    target.style.height = `${target.scrollHeight}px`;
    setTimeout(() => {
      target.style.height = null;
    }, PANEL_ANIMATION_LENGTH);
  }

  /**
   * Build loader for for dropdown
   * @param {object} panel - Panel DOM element.
   * @param {object} settings - Settings object.
   * @returns {void}
   */
  dropdownCreateSpinner(panel, settings) {
    // Get dropdown content area.
    const panelTarget = settings.target;
    if (!panelTarget) {
      return;
    }

    // See if loader already exists.
    const spinner = [...panel.getElementsByClassName(settings.panelSpinner)].length >= 1 ? [...panel.getElementsByClassName(settings.panelSpinner)][0] : null;
    if (spinner) {
      return;
    }

    // Get settings from DOM element.
    const spinnerText = settings.wrapper.getAttribute('data-spinner-text');

    // Otherwise, create dropdown.
    panel = document.createElement('div');
    panel.classList.add(settings.panelSpinner);
    panel.innerHTML = `
      <span class="spa-icon ${settings.panelSpinnerIcon} ${settings.panelSpinnerIconName} ${settings.panelSpinnerIconSize}"></span>
      ${spinnerText ? `<div class="${settings.panelSpinnerText}">${spinnerText}</div>` : ''}
    `;

    // Append dropdown to DOM.
    panelTarget.insertBefore(panel, panelTarget.childNodes[0]);
  }

  /**
   * Returns the `panel-header` element for the target squeezebox.
   * @param {object} targetSqueezebox - The squeezebox panel whose control elements need to be
   *   identified.
   * @returns {void}
   */
  findSqueezeboxControls(targetSqueezebox) {
    const targetHeader = targetSqueezebox.querySelector(`
      .panel-header[data-panel='${targetSqueezebox.id}']
    `);
    const targetControls = [];
    targetControls.push(targetHeader);
    return targetControls;
  }

  /**
   * Sets up initial event listeners.
   * @returns {void}
   */
  updateClassObject() {
    const isSafari = navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1;
    const isEdge = navigator.userAgent.indexOf('Edge') !== -1;
    this.safariOrEdge = isSafari || isEdge;
    this.scrollOffset = 0;
  }

  /**
   * Scrolls to a given element by slug ID, accounting for sticky bar height.
   * @param {string} target Page element to be scrolled into view.
   * @returns {void}
   */
  scrollTo(target) {
    // Ensure we have a valid page element to scroll to.
    if (!target) {
      return;
    }

    // Safari and Edge don't use a different `scrollTop` value.
    const currentScroll = this.safariOrEdge ? document.body.scrollTop : document.documentElement.scrollTop;

    // Figure out the particular spot we want to scroll to.
    const targetScroll = Math.max(0, target.getBoundingClientRect().top + currentScroll - this.scrollOffset);

    // Move closer to the target.
    const newScroll = currentScroll * 0.3 + targetScroll * 0.7;

    // Set the new scroll position (allowing for browser differences).
    if (this.safariOrEdge) {
      document.body.scrollTop = newScroll;
    } else {
      document.documentElement.scrollTop = newScroll;
    }
    const isAtBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight;

    // If we're not there yet, keep going. This creates the smooth scroll animation.
    if (Math.abs(currentScroll - targetScroll) > 2 && !isAtBottom) {
      setTimeout(() => {
        this.scrollTo(target);
      }, 100);
    }
  }

  /**
   * On top level collapse/show control click toggle any innerPanel squeezebox
   * @param {object|string} squeezeboxElement - DOM element or ID of squeezebox to set control of
   * @returns {void}
   */
  expandCollapseInnerPanels(squeezeboxElement) {
    // did we get an ID or element
    let wrapper = squeezeboxElement;
    if (typeof wrapper === 'string') {
      wrapper = document.getElementById(squeezeboxElement);
    }

    // kill check against instance
    if (instance[wrapper.id].allowExpandCollapseInnerPanels === false) {
      return;
    }
    if (wrapper) {
      const showAllTrigger = wrapper.querySelector('.squeezebox-expand-all-link');
      const collapseAllTrigger = wrapper.querySelector('.squeezebox-collapse-all-link');
      const allInnerPanels = [...wrapper.querySelectorAll('.innerPanels .panel-header')];
      const allInnerPanelsControls = [...wrapper.querySelectorAll('.innerPanels .squeezebox-group-control')];

      // open any and all children squeezeboxes
      if (showAllTrigger) {
        showAllTrigger.addEventListener('click', () => {
          const allChildrenShowAllTriggers = [...wrapper.querySelectorAll('.panel-header')];
          allChildrenShowAllTriggers.forEach(element => {
            this.activatePanel(element, true);
          });
        });
      }

      // collapse any and all children squeezeboxes
      if (collapseAllTrigger) {
        collapseAllTrigger.addEventListener('click', () => {
          const allChildrenCollapseAllTriggers = [...wrapper.querySelectorAll('.panel-header')];
          allChildrenCollapseAllTriggers.forEach(element => {
            this.activatePanel(element, false);
          });
        });
      }

      // create listener to update top level control if not all inner panels are visible/hidden
      allInnerPanels.concat(allInnerPanelsControls).forEach(element => {
        element.addEventListener('click', () => {
          wrapper.classList.remove('all-panels-open');
          wrapper.classList.remove('all-panels-closed');
        });
      });
    }

    // prevent future events from binding
    instance[wrapper.id].allowExpandCollapseInnerPanels = false;
  }

  /**
   * Changes status of squeezebox panels and adds/changes icons
   * @param {Array} settings - Array of objects with the id and icon to change to. I.E settings = [{ id: 'elementId', status: 'complete' }] changeIconToType has a value of complete, incomplete, error
   */
  changeStatus(settings) {
    settings.forEach(panel => {
      const {
        statusStrings = {}
      } = panel;
      const panelHeader = document.querySelector(`#${panel.id.trim()}`);
      const squeezeboxContainer = panelHeader?.parentElement;
      const hasIconEnabled = !!squeezeboxContainer?.classList.contains('spa-has-icon');
      const panelStatusIcon = document.querySelector(`.panel-header[data-panel='${panel.id.trim()}'] .panel-status-icon`);

      // avoid adding classes to panels if icons are not enabled or shown.
      if (!hasIconEnabled && !(panelStatusIcon instanceof Element) || !hasIconEnabled && !panelStatusIcon.hasAttribute('aria-label')) {
        return;
      }

      // Setup to update strings
      const panelTrigger = panelHeader.firstElementChild;
      const statusStringsObj = panelTrigger.getAttribute('data-panel-status-strings') ? JSON.parse(panelTrigger.getAttribute('data-panel-status-strings')) : {
        defaultText: panelTrigger.innerText || panelTrigger.getAttribute('name'),
        status: Object.assign(this.content?.status || {
          complete: 'complete',
          error: 'error',
          incomplete: 'incomplete'
        }, statusStrings)
      };
      const prefix = 'spa-squeezebox-panel--';
      const classes = panelHeader.className.split(' ').filter(classes => !classes.startsWith(prefix));

      // Update icon by class
      panelHeader.className = classes.join(' ').trim();
      panelHeader.classList.add(`spa-squeezebox-panel--${panel.status}`);

      // Update trigger aria-label
      panelTrigger.setAttribute('aria-label', `${statusStringsObj.defaultText} ${statusStringsObj.status[panel.status]}`.replace(/[\t\n\r]/gm, ' ').trim().replace(/\s+/g, ' '));
    });
  }

  /**
   * @returns {object} A collection of namespaced event strings for this utility.
   */
  get EVENTS() {
    return EVENTS;
  }
}
var index = new SpartaSqueezebox();

export { index as default };
